import { BookOpen, ClipboardEdit } from 'lucide-react';
import type { Avaliacao } from '../../api/hooks/useAvaliacoes';
import {
  BIMESTRES,
  TIPO_LABELS,
  STATUS_LABELS,
  formatDataCurta,
  linhaHistoricoNota,
  professorPodeEditarAvaliacao,
  motivoBloqueioEdicao,
} from './avaliacoes-professor-shared';
import type { StatusAvaliacao } from '../../api/hooks/useAvaliacoes';
import type { UseQueryResult } from '@tanstack/react-query';
import type { NotaHistorico } from '../../api/hooks/useAvaliacoes';

interface Props {
  av: Avaliacao;
  historicoNotasQuery: UseQueryResult<NotaHistorico[]>;
  onLancarNotas: (av: Avaliacao) => void;
  onMudarStatus: (id: string, status: StatusAvaliacao) => void;
  mudarStatusPending: boolean;
  variant?: 'sheet' | 'panel';
}

export function AvaliacaoDetalhePanel({
  av,
  historicoNotasQuery,
  onLancarNotas,
  onMudarStatus,
  mudarStatusPending,
  variant = 'panel',
}: Props) {
  const tipoInfo = TIPO_LABELS[av.tipo];
  const statusInfo = STATUS_LABELS[av.status];
  const bimLabel = BIMESTRES.find((b) => b.value === av.bimestre)?.label;
  const podeNotas = professorPodeEditarAvaliacao(av, 'notas');
  const podeStatus = professorPodeEditarAvaliacao(av, 'status');
  const bloqueioMsg = motivoBloqueioEdicao(av);

  return (
    <div className={variant === 'panel' ? 'aval-desk-detail-inner' : ''}>
      <div className={variant === 'sheet' ? 'comunicado-sheet-header' : 'aval-desk-detail-head'}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
            <span
              className="comunicado-sheet-badge-tipo"
              style={{
                background: `${tipoInfo.color}18`,
                color: tipoInfo.color,
                border: `1px solid ${tipoInfo.color}33`,
              }}
            >
              {tipoInfo.label}
            </span>
            <span
              className="aval-item-status"
              style={{
                background: statusInfo.bg,
                color: statusInfo.color,
                fontSize: '0.55rem',
                padding: '3px 8px',
              }}
            >
              {statusInfo.label}
            </span>
          </div>
          <h2
            id={variant === 'sheet' ? 'aval-detalhe-title' : undefined}
            className={variant === 'sheet' ? 'comunicado-sheet-title' : 'aval-desk-detail-title'}
          >
            {av.titulo}
          </h2>
          <p className="comunicado-sheet-meta-line">
            <BookOpen size={12} />
            {av.turma?.nome} · {av.materia?.nome}
            {bimLabel ? ` · ${bimLabel}` : ''}
          </p>
        </div>
      </div>

      <div className="aval-detalhe-resumo">
        <div className="aval-detalhe-stat">
          <span className="aval-detalhe-stat-value">{av._count?.notas ?? 0}</span>
          <span className="aval-detalhe-stat-label">Notas lancadas</span>
        </div>
        <div className="aval-detalhe-stat">
          <span className="aval-detalhe-stat-value">{av.peso}x</span>
          <span className="aval-detalhe-stat-label">Peso</span>
        </div>
        <div className="aval-detalhe-stat">
          <span className="aval-detalhe-stat-value">{av.valorMaximo}</span>
          <span className="aval-detalhe-stat-label">Nota maxima</span>
        </div>
      </div>

      <section className="aval-detalhe-dados" aria-label="Dados da avaliacao">
        <h3 className="aval-detalhe-dados-title">Dados da avaliacao</h3>
        <dl className="aval-detalhe-grid">
          <div className="aval-detalhe-cell">
            <dt>Tipo</dt>
            <dd>{tipoInfo.label}</dd>
          </div>
          <div className="aval-detalhe-cell">
            <dt>Bimestre</dt>
            <dd>{bimLabel || `${av.bimestre}º`}</dd>
          </div>
          <div className="aval-detalhe-cell">
            <dt>Ano letivo</dt>
            <dd>{av.anoLetivo}</dd>
          </div>
          <div className="aval-detalhe-cell">
            <dt>Aplicacao</dt>
            <dd>{av.dataAplicacao ? formatDataCurta(av.dataAplicacao) : '—'}</dd>
          </div>
          <div className="aval-detalhe-cell">
            <dt>Peso</dt>
            <dd>{av.peso}x</dd>
          </div>
          <div className="aval-detalhe-cell">
            <dt>Nota max.</dt>
            <dd>{av.valorMaximo}</dd>
          </div>
          <div className="aval-detalhe-cell aval-detalhe-cell--full">
            <dt>Turma</dt>
            <dd>{av.turma?.nome || '—'}</dd>
          </div>
          <div className="aval-detalhe-cell aval-detalhe-cell--full">
            <dt>Materia</dt>
            <dd>{av.materia?.nome || '—'}</dd>
          </div>
          {av.professor?.nome && (
            <div className="aval-detalhe-cell aval-detalhe-cell--full">
              <dt>Professor</dt>
              <dd>{av.professor.nome}</dd>
            </div>
          )}
          <div className="aval-detalhe-cell aval-detalhe-cell--full">
            <dt>Criada em</dt>
            <dd>{formatDataCurta(av.criadoEm)}</dd>
          </div>
        </dl>
      </section>

      <section className="aval-detalhe-desc-block" aria-label="Descricao">
        <h3 className="aval-detalhe-dados-title">Descricao / instrucoes</h3>
        <p className={`aval-detalhe-desc${av.descricao ? '' : ' aval-detalhe-desc--empty'}`}>
          {av.descricao?.trim() || 'Nenhuma descricao ou instrucao cadastrada.'}
        </p>
      </section>

      <section className="aval-detalhe-historico" aria-label="Historico de notas">
        <h3 className="aval-detalhe-dados-title">Historico de alteracoes</h3>
        {historicoNotasQuery.isLoading ? (
          <p className="aval-detalhe-historico-empty">Carregando historico...</p>
        ) : historicoNotasQuery.data?.length === 0 ? (
          <p className="aval-detalhe-historico-empty">Nenhuma alteracao registrada ainda.</p>
        ) : (
          <ul className="aval-detalhe-historico-list">
            {historicoNotasQuery.data?.map((h) => (
              <li key={h.id}>{linhaHistoricoNota(h)}</li>
            ))}
          </ul>
        )}
      </section>

      {bloqueioMsg && (
        <p className="aval-detalhe-bloqueio" role="status">
          {bloqueioMsg}
        </p>
      )}

      <div className="aval-detalhe-status-block">
        <label className="aval-form-label" htmlFor="aval-detalhe-status">
          Status da avaliacao
        </label>
        <select
          id="aval-detalhe-status"
          className="aval-form-select"
          value={av.status}
          disabled={mudarStatusPending || !podeStatus}
          onChange={(e) => onMudarStatus(av.id, e.target.value as StatusAvaliacao)}
        >
          {Object.entries(STATUS_LABELS).map(([k, v]) => (
            <option key={k} value={k}>
              {v.label}
            </option>
          ))}
        </select>
      </div>

      <button
        type="button"
        className="aval-detalhe-btn-notas"
        onClick={() => onLancarNotas(av)}
        disabled={!podeNotas}
        title={!podeNotas ? bloqueioMsg ?? undefined : undefined}
      >
        <ClipboardEdit size={16} aria-hidden />
        {podeNotas ? 'Lancar notas' : 'Notas bloqueadas'}
      </button>
    </div>
  );
}
